import { Checkbox } from '@/components/ui/checkbox';
import { ButtonCheckbox } from '@/components/ui/button-checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Tooltip } from '@/components/tooltip';
import { cn } from '@/lib/tailwind-utils';
import { Info } from 'lucide-react';
import { ComponentProps, ReactNode } from 'react';
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

type CheckboxPropsWithTestId = ComponentProps<typeof Checkbox> & {
  'data-testid'?: string;
};

interface FormCheckboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  label: string;
  checkboxProps?: CheckboxPropsWithTestId;
  labelTestId?: string;
  horizontal?: boolean;
  className?: string;
  labelClassName?: string;
  CheckboxComponent?: typeof Checkbox | typeof ButtonCheckbox;
  secondaryLabel?: string | ((checked: boolean) => string | undefined);
  checkboxClassName?: string;
  labelTooltip?: ReactNode;
}

export function FormCheckbox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  form,
  name,
  label,
  checkboxProps = {},
  labelTestId,
  horizontal = false,
  className = '',
  labelClassName,
  CheckboxComponent = Checkbox,
  secondaryLabel,
  checkboxClassName = '',
  labelTooltip,
}: FormCheckboxProps<TFieldValues, TName>) {
  const { required, ...others } = checkboxProps;

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem
          className={cn(
            horizontal ? 'flex flex-row-reverse items-center justify-end gap-2 space-y-0' : '',
            CheckboxComponent === ButtonCheckbox ? 'flex flex-col items-start' : '',
            className,
          )}
        >
          {horizontal && <FormMessage />}
          {CheckboxComponent !== ButtonCheckbox && (
            <div className="flex min-w-0 items-start gap-2">
              <FormLabel
                className={cn('font-bold', labelClassName, others.disabled && 'text-gray-400')}
                data-testid={labelTestId}
              >
                {label}
                {required && '*'}
              </FormLabel>
              {labelTooltip && <Tooltip trigger={<Info className="h-5 w-5" />}>{labelTooltip}</Tooltip>}
            </div>
          )}
          <div className={cn(horizontal && 'flex items-center')}>
            <FormControl>
              {CheckboxComponent === ButtonCheckbox ? (
                <ButtonCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  secondaryLabel={typeof secondaryLabel === 'function' ? secondaryLabel(field.value) : secondaryLabel}
                  className={checkboxClassName}
                  {...others}
                >
                  {label}
                </ButtonCheckbox>
              ) : (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid={others['data-testid']}
                  {...others}
                />
              )}
            </FormControl>
          </div>
          {!horizontal && <FormMessage />}
        </FormItem>
      )}
    />
  );
}
